import { Store } from '@adapters/prismic/fetching/stores/types';
import Cookies from 'js-cookie';
import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import orderByDistance from 'geolib/es/orderByDistance';
import getDistance from 'geolib/es/getDistance';

export type Location = { latitude: number; longitude: number };

export type StoreWithDistance = Store & { distanceInMeters?: number };

export interface ReducerState {
  stores?: StoreWithDistance[];
  favouriteStoreDetails: any;
}

export interface DerivedState {
  favouriteStoreDetails: any;
  stores?: StoreWithDistance[];
}

export interface Actions {
  setFavouriteStore: (selectedStore: string) => any;
  setLocationAndFavouriteStore: (location: Location) => void;
}

export type Action =
  | {
      type: 'SET_FAVOURITE_STORE';
      selectedStore: string;
    }
  | {
      type: 'SET_STORES_FROM_LOCATION';
      stores: StoreWithDistance[];
      selectedStore: string | number;
    };

export type StoresState = ReducerState & DerivedState & Actions;

type StoreOrderEntry = {
  store: string | number;
  latitude: number;
  longitude: number;
  distanceInMeters: number;
};

/**
 * Full store list (no exclude_from_spu filter) so:
 * - favourite resolution always finds the selected store document
 * - FindInStoreSection can match every key in variant.storeInventory
 * Modal / card UIs still gate actions with exclude_from_spu.
 */
export const createInitialStoresState = (
  storesData?: Store[],
  storeId?: string | boolean
) => ({
  stores: storesData,
  favouriteStoreDetails: storeId
    ? storesData?.find((item: Store) => item.data.mec_store_id === storeId) ??
      false
    : undefined,
  setFavouriteStore: () => null,
  setLocationAndFavouriteStore: () => null,
});

const orderStoresFromUserLocation = (
  userLocation: Location,
  allStores: Store[]
) => {
  const userCoords = {
    latitude: userLocation.latitude,
    longitude: userLocation.longitude,
  };

  const storesLocationCoords = allStores.map((item: Store) => {
    const latitude = item.data.geo.latitude;
    const longitude = item.data.geo.longitude;

    return {
      store: item.data.mec_store_id,
      latitude,
      longitude,
      distanceInMeters: getDistance(userCoords, { latitude, longitude }),
    };
  });

  return orderByDistance(userCoords, storesLocationCoords);
};

/** Recompute per-store distances from an anchor (e.g. favourite store) so PDP preview radius updates when the user picks a new favourite. */
const enrichStoresWithDistancesFromPoint = (
  allStores: StoreWithDistance[],
  origin: Location
): StoreWithDistance[] => {
  const { latitude: lat1, longitude: lon1 } = origin;
  const enriched = allStores.map((s) => {
    const { latitude: lat2, longitude: lon2 } = s.data.geo;
    if (
      ![lat1, lon1, lat2, lon2].every(
        (n) => typeof n === 'number' && !Number.isNaN(n)
      )
    ) {
      return { ...s, distanceInMeters: undefined };
    }
    return {
      ...s,
      distanceInMeters: getDistance(
        { latitude: lat1, longitude: lon1 },
        { latitude: lat2, longitude: lon2 }
      ),
    };
  });
  return enriched.sort((a, b) => {
    const da = a.distanceInMeters ?? Number.POSITIVE_INFINITY;
    const db = b.distanceInMeters ?? Number.POSITIVE_INFINITY;
    return da - db;
  });
};

/**
 * Same store order, distances and favourite → the dispatch changed nothing.
 * Consumers list `stores` / `favouriteStoreDetails` / the setters in effect deps,
 * so returning fresh arrays for an unchanged result loops them forever.
 */
const isUnchangedResult = (
  state: ReducerState,
  nextStores: StoreWithDistance[],
  nextFavourite: StoreWithDistance | null
) =>
  state.stores != null &&
  state.stores.length === nextStores.length &&
  state.stores.every(
    (store, index) =>
      store.data.mec_store_id === nextStores[index].data.mec_store_id &&
      store.distanceInMeters === nextStores[index].distanceInMeters
  ) &&
  (state.favouriteStoreDetails?.data?.mec_store_id ?? null) ===
    (nextFavourite?.data?.mec_store_id ?? null);

const getClosestStore = (
  allStores: StoreWithDistance[],
  currentLocation: Location
): [StoreWithDistance, StoreOrderEntry[]] | null => {
  if (
    !currentLocation ||
    !currentLocation.latitude ||
    !currentLocation.longitude
  )
    return null;

  const orderedEntries = orderStoresFromUserLocation(
    {
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
    },
    allStores
  ) as StoreOrderEntry[];

  if (!orderedEntries?.length) return null;

  const closeStore = allStores.find(
    (item) => item.data.mec_store_id === orderedEntries[0].store
  );
  if (!closeStore) return null;

  return [closeStore, orderedEntries];
};

export const StoresContext = createContext<StoresState | any>(
  createInitialStoresState()
);
StoresContext.displayName = 'StoresUIContext';

const FAVOURITE_STORE_COOKIE_NAME = 'storeId';
const ONE_DAY = 60 * 60 * 24;

const storesReducer = (state: ReducerState, action: Action) => {
  switch (action.type) {
    case 'SET_FAVOURITE_STORE': {
      if (action.selectedStore) {
        Cookies.set(FAVOURITE_STORE_COOKIE_NAME, action.selectedStore, {
          expires: ONE_DAY * 30,
        });
      }
      const favourite =
        state.stores?.find(
          (item) => item.data.mec_store_id === action.selectedStore
        ) ?? null;

      if (!favourite || !state.stores?.length) {
        return {
          ...state,
          favouriteStoreDetails: favourite,
        };
      }

      const { latitude: lat, longitude: lon } = favourite.data.geo;
      if (
        typeof lat !== 'number' ||
        typeof lon !== 'number' ||
        Number.isNaN(lat) ||
        Number.isNaN(lon)
      ) {
        return {
          ...state,
          favouriteStoreDetails: favourite,
        };
      }

      const enrichedStores = enrichStoresWithDistancesFromPoint(state.stores, {
        latitude: lat,
        longitude: lon,
      });

      if (isUnchangedResult(state, enrichedStores, favourite)) {
        return state;
      }

      return {
        ...state,
        stores: enrichedStores,
        favouriteStoreDetails: favourite,
      };
    }
    case 'SET_STORES_FROM_LOCATION': {
      if (action.selectedStore !== '' && action.selectedStore != null) {
        Cookies.set(FAVOURITE_STORE_COOKIE_NAME, String(action.selectedStore), {
          expires: ONE_DAY * 30,
        });
      }
      const favourite =
        action.stores.find(
          (item) => item.data.mec_store_id === action.selectedStore
        ) ?? null;

      if (isUnchangedResult(state, action.stores, favourite)) {
        return state;
      }

      return {
        ...state,
        stores: action.stores,
        favouriteStoreDetails: favourite,
      };
    }
    default: {
      return { ...state };
    }
  }
};

export type StoresUIContextProps = {
  stores: Store[];
  children: ReactNode;
};

export const StoresUIContext = ({ stores, children }: StoresUIContextProps) => {
  const initialStoresState = createInitialStoresState(stores, 'none');

  const [state, dispatch] = useReducer(storesReducer, initialStoresState);

  /** Read the latest stores without making the setters change identity every dispatch. */
  const storesRef = useRef(state.stores);
  storesRef.current = state.stores;

  const setFavouriteStore = useCallback(
    (selectedStore: string) =>
      dispatch({ type: 'SET_FAVOURITE_STORE', selectedStore }),
    []
  );

  useEffect(() => {
    const cookieStoreId = Cookies.get('storeId');
    if (cookieStoreId) {
      setFavouriteStore(cookieStoreId);
    }
  }, [setFavouriteStore]);

  const setLocationAndFavouriteStore = useCallback((location: Location) => {
    const allStores = storesRef.current;
    if (!allStores?.length) return;

    const closestResult = getClosestStore(allStores, location);
    if (!closestResult) return;

    const [closestStore, orderedEntries] = closestResult;
    const distanceById = new Map(
      orderedEntries.map((e) => [e.store, e.distanceInMeters] as const)
    );
    const idOrder = orderedEntries.map((e) => e.store);

    const enrichedStores: StoreWithDistance[] = [...allStores]
      .map((s) => ({
        ...s,
        distanceInMeters: distanceById.get(s.data.mec_store_id),
      }))
      .sort((a, b) => {
        const ia = idOrder.indexOf(a.data.mec_store_id);
        const ib = idOrder.indexOf(b.data.mec_store_id);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });

    dispatch({
      type: 'SET_STORES_FROM_LOCATION',
      stores: enrichedStores,
      selectedStore: closestStore.data.mec_store_id,
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setFavouriteStore,
      setLocationAndFavouriteStore,
    }),
    [setFavouriteStore, setLocationAndFavouriteStore, state]
  );

  return (
    <StoresContext.Provider value={value}>{children}</StoresContext.Provider>
  );
};

export const StoresUIContextProvider = ({
  children,
  stores,
}: {
  children: ReactNode;
  stores: Store[];
}) => <StoresUIContext stores={stores}>{children}</StoresUIContext>;

export const useStoresUI = () => {
  const context = useContext(StoresContext);
  if (context === undefined) {
    throw new Error('useStoresUI must be used within a StoresUIProvider');
  }
  return context;
};
